/* eslint-disable spire/export-styles */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    backgroundColor = "backgroundColor",
    backgroundSize = "backgroundSize",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.backgroundColor]: string;
        [fields.background]: "image" | "color";
        [fields.backgroundSize]: "auto" | "cover" | "contain" | "100% 100%";
    };
}

export interface PageBannerStyles {
    overlay?: TypographyProps;
}

export const pagebannerStyles: PageBannerStyles = {
    overlay: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding-left: 31px;
            position: absolute;
            top: 0;
            left: 0;
            width: 34%;
            height: 100%;
            background-color: #44a8f7;
            background-image: linear-gradient(to right, #44a8f7, #0b70b7);
            opacity: 0.9;
            .heading {
                h1 {
                    width: 100%;
                    font-size: 36px;
                    line-height: 0.9;
                    margin-bottom: 15px;
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    color: ${COLORS.white};
                }
                p {
                    font-size: ${TYPOGRAPHY.size.large};
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    color: ${COLORS.white};
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            .CSR {
                                h1 {
                                    font-size: 36px !important;
                                }
                            }
                        `,
                        css`
                            .CSR {
                                h1 {
                                    font-size: 26px;
                                }
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = pagebannerStyles;

type Props = OwnProps;

const PageBanner: FC<Props> = ({ id, fields }) => {
    const pagebannerStyles =
        fields.background === "image"
            ? `background-image: url(${fields.image}); background-size: ${fields.backgroundSize}`
            : `background-color: ${fields.backgroundColor};`;

    const slideWrapperStyles = {
        css: css`
            height: 400px;
            position: relative;
            background-position: center center;
            ${pagebannerStyles}
        `,
    };
    return (
        <StyledWrapper {...slideWrapperStyles}>
            <StyledWrapper {...styles.overlay}>
                <Zone contentId={id} zoneName="content" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const pageBanner: WidgetModule = {
    component: PageBanner,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
                fieldType: "Translatable",
            },
            {
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                fieldType: "Translatable",
            },
            {
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
                fieldType: "Translatable",
            },
            {
                name: fields.backgroundSize,
                displayName: "Background Size",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Auto", value: "auto" },
                    { displayName: "Cover", value: "cover" },
                    { displayName: "Contain", value: "contain" },
                    { displayName: "100% X 100%", value: "100% 100%" },
                ],
                defaultValue: "auto",
                hideEmptyOption: true,
                fieldType: "Translatable",
            },
        ],
    },
};

export default pageBanner;
